import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

/**
 * Validador de códigos de barras que soporta múltiples formatos
 * Implementa algoritmos estándar de validación de códigos de barras
 */
export class BarcodeValidator {

  /**
   * Validador que acepta múltiples formatos de códigos de barras
   * @param formats Array de formatos permitidos. Por defecto acepta todos los formatos comunes
   * @returns ValidatorFn para usar en reactive forms
   */
  static validate(formats: BarcodeFormat[] = ['EAN13', 'EAN8', 'UPCA', 'CODE128']): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      
      // Si el campo está vacío, no validamos (usar Validators.required por separado)
      if (!value || value.trim() === '') {
        return null;
      }

      const barcode = value.trim();
      const validationResult = BarcodeValidator.validateBarcode(barcode, formats);
      
      if (validationResult.isValid) {
        return null; // Código válido
      }

      // Retornar error con información específica
      return {
        invalidBarcode: {
          value: barcode,
          message: validationResult.message,
          testedFormats: formats,
          actualValue: value
        }
      };
    };
  }

  /**
   * Validar código de barras contra formatos específicos
   * @param barcode Código de barras a validar
   * @param formats Formatos a probar
   * @returns Resultado de validación con detalles
   */
  static validateBarcode(barcode: string, formats: BarcodeFormat[]): BarcodeValidationResult {
    if (!barcode || barcode.trim() === '') {
      return {
        isValid: false,
        message: 'El código de barras no puede estar vacío',
        validFormats: [],
        testedFormats: formats
      };
    }

    const cleanBarcode = barcode.trim();
    const validFormats: BarcodeFormat[] = [];
    const errors: string[] = [];

    // Probar cada formato solicitado
    for (const format of formats) {
      try {
        let isValid = false;
        
        switch (format) {
          case 'EAN13':
            isValid = BarcodeValidator.isValidEan13(cleanBarcode);
            break;
          case 'EAN8':
            isValid = BarcodeValidator.isValidEan8(cleanBarcode);
            break;
          case 'UPCA':
            isValid = BarcodeValidator.isValidUpcA(cleanBarcode);
            break;
          case 'CODE128':
            isValid = BarcodeValidator.isValidCode128(cleanBarcode);
            break;
          default:
            errors.push(`Formato desconocido: ${format}`);
            continue;
        }

        if (isValid) {
          validFormats.push(format);
        }
      } catch (error) {
        errors.push(`Error validando ${format}: ${error.message}`);
      }
    }

    const isValid = validFormats.length > 0;
    
    return {
      isValid,
      message: isValid 
        ? `Código válido como: ${validFormats.join(', ')}`
        : `Código inválido. Formatos probados: ${formats.join(', ')}. ${errors.length > 0 ? 'Errores: ' + errors.join('; ') : ''}`,
      validFormats,
      testedFormats: formats
    };
  }

  /**
   * Validar EAN-13 usando algoritmo de checksum estándar
   */
  private static isValidEan13(barcode: string): boolean {
    // Debe tener exactamente 13 dígitos
    if (!/^\d{13}$/.test(barcode)) {
      return false;
    }

    const digits = barcode.split('').map(Number);
    const checkDigit = digits[12];
    
    // Calcular checksum usando algoritmo EAN-13
    let sum = 0;
    for (let i = 0; i < 12; i++) {
      sum += digits[i] * (i % 2 === 0 ? 1 : 3);
    }
    
    const calculatedCheckDigit = (10 - (sum % 10)) % 10;
    return calculatedCheckDigit === checkDigit;
  }

  /**
   * Validar EAN-8 usando algoritmo de checksum estándar
   */
  private static isValidEan8(barcode: string): boolean {
    // Debe tener exactamente 8 dígitos
    if (!/^\d{8}$/.test(barcode)) {
      return false;
    }

    const digits = barcode.split('').map(Number);
    const checkDigit = digits[7];
    
    // Calcular checksum usando algoritmo EAN-8
    let sum = 0;
    for (let i = 0; i < 7; i++) {
      sum += digits[i] * (i % 2 === 0 ? 3 : 1);
    }
    
    const calculatedCheckDigit = (10 - (sum % 10)) % 10;
    return calculatedCheckDigit === checkDigit;
  }

  /**
   * Validar UPC-A usando algoritmo de checksum estándar
   */
  private static isValidUpcA(barcode: string): boolean {
    // Debe tener exactamente 12 dígitos
    if (!/^\d{12}$/.test(barcode)) {
      return false;
    }

    const digits = barcode.split('').map(Number);
    const checkDigit = digits[11];
    
    // Calcular checksum usando algoritmo UPC-A
    let sum = 0;
    for (let i = 0; i < 11; i++) {
      sum += digits[i] * (i % 2 === 0 ? 3 : 1);
    }
    
    const calculatedCheckDigit = (10 - (sum % 10)) % 10;
    return calculatedCheckDigit === checkDigit;
  }

  /**
   * Validar Code 128 - Validación más estricta
   * Code 128 requiere al menos 6 caracteres para ser un código válido
   */
  private static isValidCode128(barcode: string): boolean {
    // Code 128 debe tener al menos 6 caracteres para ser válido
    if (!barcode || barcode.length < 6 || barcode.length > 48) {
      return false;
    }
    
    // Caracteres válidos para Code 128: 0-9, A-Z, espacio y algunos símbolos
    const validChars = /^[0-9A-Z \-\.,$\/\+%]*$/;
    return validChars.test(barcode);
  }

  /**
   * Obtener información detallada sobre un código de barras
   */
  static getBarcodeInfo(barcode: string): BarcodeInfo {
    if (!barcode || barcode.trim() === '') {
      return {
        value: barcode,
        length: 0,
        formats: [],
        isValid: false,
        recommendations: ['Ingrese un código de barras válido']
      };
    }

    const cleanBarcode = barcode.trim();
    const length = cleanBarcode.length;
    const formats: BarcodeFormat[] = [];
    const recommendations: string[] = [];

    // Detectar formatos posibles basados en longitud y contenido
    if (length === 13 && /^\d+$/.test(cleanBarcode)) {
      if (BarcodeValidator.isValidEan13(cleanBarcode)) {
        formats.push('EAN13');
      }
    }

    if (length === 8 && /^\d+$/.test(cleanBarcode)) {
      if (BarcodeValidator.isValidEan8(cleanBarcode)) {
        formats.push('EAN8');
      }
    }

    if (length === 12 && /^\d+$/.test(cleanBarcode)) {
      if (BarcodeValidator.isValidUpcA(cleanBarcode)) {
        formats.push('UPCA');
      }
    }

    // Code 128 puede tener longitudes variables
    if (BarcodeValidator.isValidCode128(cleanBarcode)) {
      formats.push('CODE128');
    }

    // Generar recomendaciones
    if (formats.length === 0) {
      if (length < 8) {
        recommendations.push('Los códigos de barras deben tener al menos 8 dígitos');
      } else if (length > 13 && !/[A-Za-z]/.test(cleanBarcode)) {
        recommendations.push('Los códigos numéricos no deben exceder 13 dígitos (EAN-13)');
      } else if (!/^\d+$/.test(cleanBarcode) && !/^[A-Za-z0-9\-\s]+$/.test(cleanBarcode)) {
        recommendations.push('El código contiene caracteres no válidos');
      } else {
        recommendations.push('Verifique que el código sea correcto y tenga el formato válido');
      }
    }

    return {
      value: cleanBarcode,
      length,
      formats,
      isValid: formats.length > 0,
      recommendations
    };
  }
}

/**
 * Tipos de formatos de códigos de barras soportados
 */
export type BarcodeFormat = 'EAN13' | 'EAN8' | 'UPCA' | 'CODE128';

/**
 * Resultado de validación de código de barras
 */
export interface BarcodeValidationResult {
  isValid: boolean;
  message: string;
  validFormats: BarcodeFormat[];
  testedFormats: BarcodeFormat[];
}

/**
 * Información detallada sobre un código de barras
 */
export interface BarcodeInfo {
  value: string;
  length: number;
  formats: BarcodeFormat[];
  isValid: boolean;
  recommendations: string[];
}

/**
 * Validadores específicos para cada formato (para uso individual)
 */
export const BarcodeValidators = {
  /**
   * Validador solo para EAN-13
   */
  ean13: (): ValidatorFn => BarcodeValidator.validate(['EAN13']),

  /**
   * Validador solo para EAN-8
   */
  ean8: (): ValidatorFn => BarcodeValidator.validate(['EAN8']),

  /**
   * Validador solo para UPC-A
   */
  upcA: (): ValidatorFn => BarcodeValidator.validate(['UPCA']),

  /**
   * Validador solo para Code 128
   */
  code128: (): ValidatorFn => BarcodeValidator.validate(['CODE128']),

  /**
   * Validador para códigos EAN (EAN-13 y EAN-8)
   */
  ean: (): ValidatorFn => BarcodeValidator.validate(['EAN13', 'EAN8']),

  /**
   * Validador para todos los formatos comunes
   */
  any: (): ValidatorFn => BarcodeValidator.validate(['EAN13', 'EAN8', 'UPCA', 'CODE128'])
};